import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import './TimeScrollPicker.scss';

interface Props {
  /** 24-hour time string "HH:MM" or empty string */
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function snapMinute(value: string): string {
  if (!MINUTES.includes(value)) {
    const n = Number(value);
    if (Number.isNaN(n)) return '00';
    return String(Math.round(n / 5) * 5).padStart(2, '0').replace('60', '55');
  }
  return value;
}

const TimeScrollPicker: React.FC<Props> = ({ value, onChange, onClose }) => {
  const { formatMessage: t } = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const initial = value || '09:00';
  const [hour, setHour] = useState(initial.slice(0, 2));
  const [minute, setMinute] = useState(snapMinute(initial.slice(3, 5)));

  useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="tsp" ref={ref} role="dialog" aria-label="Time picker">
      <div className="tsp__columns">
        <div className="tsp__col tsp__col--list">
          {HOURS.map((item) => (
            <button
              key={`h-${item}`}
              type="button"
              className={`tsp__choice${item === hour ? ' tsp__choice--selected' : ''}`}
              onClick={() => setHour(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="tsp__col tsp__col--list">
          {MINUTES.map((item) => (
            <button
              key={`m-${item}`}
              type="button"
              className={`tsp__choice${item === minute ? ' tsp__choice--selected' : ''}`}
              onClick={() => setMinute(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="tsp__confirm"
        onClick={() => {
          onChange(`${hour}:${minute}`);
          onClose();
        }}
      >
        {t({ id: 'daily.todoModal.timeConfirm' })}
      </button>
    </div>
  );
};

export default TimeScrollPicker;
